import { SortOrder } from 'antd/es/table/interface';
import { ColumnType } from 'antd/lib/table';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';

import { normalizeSortParams } from 'entities/Sort/normalize';
import { TableParamsChangeConfig } from 'entities/common';
import { TableBodyRequestApi } from 'entities/endpoints/enum.table_body';
import { ILocalStore } from 'stores/interfaces';

import { TableCellController } from '../../TableController';
import {
  TableFactoryHeaderStore,
  ITableColumnModel,
} from '../TableFactoryHeaderStore';
import { TableFactoryRowModel } from '../TableFactoryRowModel';
import { RowModelCache } from '../RowModelCache';
import { TableRowDataStore } from '../TableRowDataStore';

import { TableFactoryPubSub } from './PubSub';
import { TableAggregationStore } from './TableAggregationFactoryStore';
import { TABLE_FACTORY_DEFAULT_PARAMS } from './config';
import { ITableFactoryStore } from './interface';
import { TableFactoryStoreParams } from './types';

type PrivateFields =
  | '_callParams'
  | '_activeCommentEntity'
  | '_setActiveCommentEntity'
  | '_sortDirection'
  | '_virtualizedMode';

/** Общий стор для универсальной таблицы */
class TableFactoryStore implements ILocalStore, ITableFactoryStore {
  constructor(params: TableFactoryStoreParams = TABLE_FACTORY_DEFAULT_PARAMS) {
    this.tableParams = params;
    this._virtualizedMode = params.virtualized ?? false;
    this.rowModelCache = new RowModelCache(this.rowDataStore, this);

    makeObservable<TableFactoryStore, PrivateFields>(this, {
      init: action.bound,
      rows: computed,
      columns: computed,
      renderColumns: computed,
      header: computed,

      fetching: computed,

      expandedRowsKeys: computed,
      subRowsMap: computed,

      _callParams: observable.ref,
      callParams: computed,

      _sortDirection: observable.ref,
      sortDirection: computed,

      _activeCommentEntity: observable.ref,
      _setActiveCommentEntity: action.bound,
      activeCommentEntity: computed,

      _virtualizedMode: observable.ref,
      setVirtualizedMode: action.bound,

      handleTableParamsChange: action.bound,
    });

    this._subscribers.push(
      this.pubSub.subscribeToCommentUnselect(() => {
        this._setActiveCommentEntity(null);
      })
    );
    this._subscribers.push(
      this.pubSub.subscribeToCommentSelectEvent((entity) => {
        this._setActiveCommentEntity(entity);
      })
    );
  }

  private _lastUpdateDate: string;

  private readonly _subscribers: VoidFunction[] = [];

  private _sortDirection: SortOrder = 'descend';

  readonly tableHeaderStore = new TableFactoryHeaderStore({
    factoryStore: this,
  });

  /** Текущая активная сущность комментария */
  private _activeCommentEntity: TableCellController | null = null;

  readonly tableAggregationStore = new TableAggregationStore({
    factoryStore: this,
  });

  /** Store for raw row entity data (for virtualization) */
  readonly rowDataStore = new TableRowDataStore();

  /** Cache for row models (lazy creation for visible rows) */
  readonly rowModelCache: RowModelCache;

  /** Параметры, с которыми вызываем запрос */
  private _callParams: TableBodyRequestApi | null = null;

  /** Virtualized mode - skip legacy row model creation */
  private _virtualizedMode = false;

  readonly tableParams: TableFactoryStoreParams;

  readonly pubSub = new TableFactoryPubSub();

  /** Модель корневой строки, от которой идёт загрузка всех строк верхнего уровня */
  private readonly _rootRow: TableFactoryRowModel = new TableFactoryRowModel({
    entity: null,
    parent: null,
    formTableStore: this,
  });

  get lastUpdateDate() {
    return this._lastUpdateDate;
  }

  get rootRow() {
    return this._rootRow;
  }

  get sortDirection() {
    return this._sortDirection;
  }

  get activeCommentEntity() {
    return this._activeCommentEntity;
  }

  /** Всё дерево дочерних моделей строк, преобразованное в массив */
  get subRows(): TableFactoryRowModel[] {
    return this._rootRow.subRows;
  }

  get subRowsMap(): Record<string, TableFactoryRowModel> {
    return this._rootRow.subRowsMap;
  }

  get columns() {
    return this.tableHeaderStore.columns;
  }

  get renderColumns(): ITableColumnModel[] {
    return this.tableHeaderStore.columns.filter((item) => item.visible);
  }

  get header() {
    return this.tableHeaderStore.header;
  }

  get rows() {
    return this._rootRow.children;
  }

  /** Get row IDs for virtualized rendering */
  get rowIds(): string[] {
    return this.rowDataStore.rowIds;
  }

  /**
   * Get a row model by ID (lazy creation for virtualization).
   * If the model doesn't exist in cache, it will be created.
   */
  getRowModel(rowId: string): TableFactoryRowModel | null {
    return this.rowModelCache.getOrCreate(rowId);
  }

  /**
   * Set the currently visible row IDs for cleanup scheduling.
   */
  setVisibleRows(rowIds: string[]): void {
    this.rowModelCache.setVisibleRows(rowIds);
  }

  /**
   * Get virtualized mode status.
   */
  get virtualizedMode(): boolean {
    return this._virtualizedMode;
  }

  /**
   * Enable or disable virtualized mode.
   * When enabled, row models are created lazily only for visible rows.
   */
  setVirtualizedMode(enabled: boolean): void {
    this._virtualizedMode = enabled;
  }

  /** Подгружаем или нет тело таблицы */
  get fetching() {
    return (
      this._rootRow.fetchRowsRequest.loading ||
      this.tableHeaderStore.initRequest.loading
    );
  }

  get callParams() {
    return this._callParams;
  }

  /** Массив раскрытых ячеек в таблице */
  get expandedRowsKeys(): string[] {
    return this.subRows
      .filter((item) => item.expandedState.opened)
      .map((item) => item.uniqueId);
  }

  private _setActiveCommentEntity(entity: TableCellController | null) {
    this._activeCommentEntity = entity;
  }

  cancelInit(): void {
    this.tableHeaderStore.initRequest.cancel();
    this._rootRow.fetchRowsRequest.cancel();
  }

  /**
   * @param tableParams - Массив изменившихся параметров таблицы ( сортировка, фильтрация и т.п )
   * */
  async handleTableParamsChange({
    sort,
  }: TableParamsChangeConfig): Promise<void> {
    let order: SortOrder | undefined;

    let column: ColumnType<any> | undefined;

    if (sort instanceof Array) {
      ({ order, column } = sort[0]);
    } else {
      column = sort.column;
      order = sort.order;
    }

    if (this._callParams) {
      runInAction(() => {
        this._sortDirection = order || null;
      });

      this._callParams = {
        ...this._callParams,
        sort: normalizeSortParams(String(column?.dataIndex), order),
      };
    }

    await this._rootRow.fetchSubtree();
  }

  async refresh(): Promise<void> {
    const [updateResponse] = await Promise.all([
      this._rootRow.fetchSubtree(),
      this.tableAggregationStore.fetchAggregation(),
    ]);

    runInAction(() => {
      if (!updateResponse || !this._callParams) {
        return;
      }

      const { lastUpdateDate, rootRecordEntityId } = updateResponse;

      this._callParams = {
        ...this._callParams,
        root_record_id: rootRecordEntityId,
      };
      this._lastUpdateDate = lastUpdateDate;
    });
  }

  async init(params: TableBodyRequestApi): Promise<void> {
    this._callParams = params;
    await this.tableHeaderStore.init(params);
    await this.refresh();
    await this._rootRow.updateManager.start();
  }

  destroy() {
    this.tableHeaderStore.destroy();
    this._rootRow.destroy();
    this.rows.forEach((item) => item.destroy());
    this.pubSub.destroy();
    this._subscribers.forEach((sub) => sub());
    this.tableAggregationStore.destroy();
    this.rowModelCache.destroy();
    this.rowDataStore.destroy();
  }
}

export default TableFactoryStore;
